import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { HttpService } from '@bases/http.service'
import { API_URL } from '@core/constants/constant.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import { LayerSource } from '@core/models/layers.model'
import { CMSessionService } from '@core/services/cm-session.service'
import { SelectionToolService } from '@core/services/selection-tool.service'
import { environment } from '@env/environment'
import {
  CalculationModulePayload,
  CalculationModuleSignature,
  CMInput,
  CMInputType,
  CMLayerInput,
  CMSession,
  DynamicCMInputArray,
  DynamicCMInputOptions,
  LayerSelectionOption,
} from '../models/calculation-module.model'
import { UploadedLayersService } from './layers-uploaded.service'
import { LayersService } from './layers.service'
import { SelectionScaleService } from './selection-scale.service'
import { SessionManagerService } from './session-manager.service'

@Injectable({
  providedIn: 'root',
})
export class CalculationModuleService extends HttpService {
  private readonly _selectionToolsService = inject(SelectionToolService)
  private readonly _cmSessionService = inject(CMSessionService)
  private readonly _layerService = inject(LayersService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _selectionScaleService = inject(SelectionScaleService)

  protected _baseUrl = API_URL + '/cm'

  calculationModulesState$ = signal<AsyncState<CalculationModuleSignature[]>>(asyncLoading())
  calculationModules$ = computed(() => {
    const state = this.calculationModulesState$()
    return state.status === 'success' ? state.data : []
  })

  cmSelected$ = signal<CalculationModuleSignature | null>(null)
  // Untouched CM inputs returned by the API
  cmInputsOriginal$ = signal<CMInputType[]>([])
  // CM inputs used to store user entered values
  cmInputsState$ = signal<AsyncState<CMInputType[]>>(asyncLoading())
  cmInputs$ = computed(() => {
    const state = this.cmInputsState$()
    return state.status === 'success' ? state.data : []
  })

  currentCMPayload$ = signal<CalculationModulePayload | null>(null)
  currentCMTaskId$ = signal<string>('')

  isCMRunning$ = signal(false)
  readonly sessionManagerService = inject(SessionManagerService)

  isLoggedIn$ = this.sessionManagerService.isLoggedIn$

  isOnlyCMInputs$ = computed(() => {
    return this.cmInputsOriginal$().every(
      (input) => !this.isDynamicCMInputArray(input) && !this.isDynamicCMInputOptions(input),
    )
  })

  // Recursively parse all inputs and dynamic inputs to get k:v for payload
  readonly cmPayloadInputsComputed$ = computed<Record<string, any>>(() => {
    const payload: Record<string, any> = {}

    const processInput = (input: CMInputType) => {
      if (this.isDynamicCMInputArray(input)) {
        if (!input.ArrayOfRegularInput) {
          return {}
        }
        const values = input.ArrayOfRegularInput.map((i) => i.input_value)
        payload[input.input_parameter_name] = values
      } else if (this.isDynamicCMInputOptions(input)) {
        const parent = input.parent_input
        const selectedPath = parent.input_value

        // Save the parent input
        if (!this.isCMLayerInput(parent)) {
          payload[parent.input_parameter_name] = selectedPath
        }

        // Recurse only on the selected children
        const children = input[selectedPath] ?? []
        for (const child of children) {
          processInput(child)
        }
      } else if (this.isCMLayerInput(input)) {
        // ignore CMLayerInput, layer payload is handled in another computed
      } else {
        // Regular input
        payload[input.input_parameter_name] = input.input_value
      }
    }

    for (const input of this.cmInputs$()) {
      processInput(input)
    }

    return payload
  })

  // Recursively parse all layer inputs and dynamic inputs to layers data for payload
  readonly cmPayloadLayerInputsComputed$ = computed<LayerSelectionOption[]>(() => {
    const layerPayloads: LayerSelectionOption[] = []

    const collectLayerInputs = (input: CMInputType) => {
      if (this.isDynamicCMInputArray(input)) {
        // Array input not suported for layers yet
      } else if (this.isDynamicCMInputOptions(input)) {
        const parent = input.parent_input

        // Check if the parent itself is a CMLayerInput
        if (this.isCMLayerInput(parent)) {
          layerPayloads.push(
            this._serializeLayerForPayload(
              parent.layerSelected,
              parent.input_parameter_name,
              parent.data_type,
            ),
          )
        }

        const selectedPath = parent.input_value

        const children = input[selectedPath] ?? []
        for (const child of children) {
          collectLayerInputs(child)
        }
      } else if (this.isCMLayerInput(input)) {
        layerPayloads.push(
          this._serializeLayerForPayload(
            input.layerSelected,
            input.input_parameter_name,
            input.data_type,
          ),
        )
      }
      // else: ignore other input types
    }

    for (const input of this.cmInputs$()) {
      collectLayerInputs(input)
    }

    return layerPayloads
  })

  private _serializeLayerForPayload(
    layer: LayerSource,
    input_parameter_name: string,
    data_type: string,
  ): LayerSelectionOption {
    return {
      id: layer?.id,
      name: layer?.name,
      workspacename: layer?.workspacename ?? layer?.layer ?? '',
      layer: layer?.layer ?? '',
      downloadUrl: layer?.downloadUrl ?? '',
      layer_type: layer?.layer_type,
      data_type,
      input_parameter_name,
    }
  }

  constructor() {
    super()
    window.addEventListener('beforeunload', this._handleBeforeUnload)
    window.addEventListener('pagehide', this._handleBeforeUnload)

    effect(() => {
      if (this.cmSelected$() === null) {
        this.resetCurrentCM()
      }
    })
  }

  resetCurrentCM() {
    this.cancelRunCM()
    this.setCurrentCMPayload(null)
    this.resetCMInputs()
  }

  private _pollingTimeout: any = null // Stores timeout ID
  private _pollingInterval = 1000

  private _handleBeforeUnload = () => {
    const taskId = this.currentCMTaskId$()
    if (!taskId || !this.isCMRunning$()) return

    fetch(this._baseUrl + '/delete/' + taskId, {
      method: 'DELETE',
      keepalive: true,
      credentials: 'include',
    })
  }

  async getCalculationModuleList(): Promise<any> {
    if (this.calculationModulesState$().status === 'success') return // already loaded, avoid re-triggering the loader (and losing scroll targets) each time the list remounts

    this.calculationModulesState$.set(asyncLoading())
    const response = await this.GETlist()

    if (response === null) {
      this.calculationModulesState$.set(asyncError())
      return
    }
    const cmsFiltered = (response as CalculationModuleSignature[])
      .filter((cm) => environment.cmsId.includes(cm.cm_id))
      .sort(
        (cmA, cmB) => environment.cmsId.indexOf(cmA.cm_id) - environment.cmsId.indexOf(cmB.cm_id),
      ) // order of the environment is use for display order

    this.calculationModulesState$.set(asyncSuccess(cmsFiltered))
  }

  initCMInputs(cmId: string): void {
    this.cmInputsState$.set(asyncLoading())

    const selectedCMData = this.calculationModules$().find((cm) => cm.cm_id === cmId)
    const fetchedInputs = selectedCMData?.inputs_calculation_module as CMInput[]

    if (!fetchedInputs) {
      this.cmInputsState$.set(asyncError())
      return
    }

    // Store the original untouched input signature
    this.cmInputsOriginal$.set(fetchedInputs)

    // Process layer inputs
    const processedLayerInputs = fetchedInputs.map((input) => {
      if (this.isCMLayerInput(input)) {
        // Get layers based on input_options_array
        const layers = this._processLayerInput(input)
        return {
          ...input,
          layers,
          layerSelected: layers[0],
          data_type: input.layer_format,
        }
      }

      return input
    })

    // Process dynamic inputs
    const processedDynamicInputs =
      this.populateArrayOfRegularInputInDynamicCMInputArray(processedLayerInputs)

    // Set a deep cloned copy to be editable in the UI
    this._setCmInputs(processedDynamicInputs)
  }

  populateArrayOfRegularInputInDynamicCMInputArray(inputs: CMInputType[]): CMInputType[] {
    return inputs.map((input) => {
      if (this.isDynamicCMInputArray(input)) {
        const arrayInput = input

        if (!arrayInput.ArrayOfRegularInput) {
          arrayInput.ArrayOfRegularInput = []

          const count = arrayInput.default_inputs_amount ?? 1
          for (let i = 0; i < count; i++) {
            const template = arrayInput.input_template
            template.input_name += ` ${i + 1}`
            template.input_parameter_name += `_${i}`

            // Also apply recursively to dynamic children inside template, if any
            const [populated] = this.populateArrayOfRegularInputInDynamicCMInputArray([template])
            arrayInput.ArrayOfRegularInput.push(populated as CMInput)
          }
        }

        return arrayInput
      }

      if (this.isDynamicCMInputOptions(input)) {
        const optionsInput = input

        // Handle parent input if it's a layer input
        if (this.isCMLayerInput(optionsInput.parent_input)) {
          const layerInput = optionsInput.parent_input
          const layers = this._processLayerInput(layerInput)
          optionsInput.parent_input = {
            ...layerInput,
            layers,
            layerSelected: layers[0],
            data_type: layerInput.layer_format,
          }
        }

        // Process children inputs
        for (const key of Object.keys(optionsInput)) {
          // Skip metadata keys
          if (['input_name', 'input_priority', 'parent_input'].includes(key)) continue

          const pathInputs = optionsInput[key]
          if (Array.isArray(pathInputs)) {
            optionsInput[key] = this.populateArrayOfRegularInputInDynamicCMInputArray(pathInputs)
          }
        }

        return optionsInput
      }

      // Handle regular CMLayerInput
      if (this.isCMLayerInput(input)) {
        const layerInput = input
        const layers = this._processLayerInput(layerInput)

        return {
          ...layerInput,
          layers,
          layerSelected: layers[0],
          data_type: layerInput.layer_format,
        }
      }

      // Regular CMInput
      return input
    })
  }

  resetCMInputs() {
    this.cmInputsOriginal$.set([])
    this.cmInputsState$.set(asyncLoading())
  }

  private _setCmInputs(inputs: CMInputType[]) {
    this.cmInputsState$.set(asyncSuccess(inputs))
  }

  getPayloadSizeInBytes(payload: CalculationModulePayload): number {
    return new Blob([JSON.stringify(payload)]).size
  }

  async runCM() {
    this.isCMRunning$.set(true)

    const response: { status_id: string } = await this.POST(
      this.currentCMPayload$(),
      this._baseUrl + '/compute-async/',
    )

    if (response === null) {
      this.isCMRunning$.set(false)
      return
    }

    if (response.status_id) {
      this.currentCMTaskId$.set(response.status_id)
      await this._pollCMStatus()
    }
  }

  async cancelRunCM() {
    if (!this.isCMRunning$()) {
      return
    }
    const id = this.currentCMTaskId$()
    const payload = this.currentCMPayload$()
    const stoppedCMName = payload ? payload.cm_name + ' - ' + payload.session_name : 'CM'

    await this.DELETE(id, this._baseUrl + '/delete/' + id)
    this.isCMRunning$.set(false)

    this._toasterService.openSnackBar(stoppedCMName + ' STOPPED')
  }

  //@todo, messy, should be cleaned so the payload is not set imperatively
  setCurrentCMPayload(cm: CalculationModuleSignature | null) {
    const scaleLevel = this._selectionScaleService.currentScaleLevel$()
    if (cm === null || !scaleLevel) {
      this.currentCMPayload$.set(null)
      return
    }

    const payload: CalculationModulePayload = {
      scale: scaleLevel.apiName,
      inputs: this.cmPayloadInputsComputed$(),
      cm_id: '' + cm.cm_id,
      cm_name: cm.cm_name,
      session_name: cm.cm_prefix,
      areas: this._selectionToolsService.getNutsOrAreas(),
      geojson_selection: this._selectionToolsService.geoJsonSelection$(),
      layers_needed: this.cmPayloadLayerInputsComputed$(),
      inputs_signature: this.cmInputsOriginal$(),
    }
    this.currentCMPayload$.set(payload)
  }

  isDynamicCMInputArray(input: any): input is DynamicCMInputArray {
    return input && 'input_template' in input
  }

  isDynamicCMInputOptions(input: any): input is DynamicCMInputOptions {
    return input && 'parent_input' in input
  }

  isCMLayerInput(input: any): input is CMLayerInput {
    return input && input.input_type === 'layer'
  }

  // Updates a regular CMInput
  updateInputValue(targetInput: CMInput, newValue: string | number) {
    const updateRecursively = (input: CMInputType): typeof input => {
      if (this.isCMLayerInput(input)) {
        return input
      }
      // Direct match for CMInput at root level
      if (input === targetInput && 'input_value' in input) {
        return { ...input, input_value: newValue }
      }

      // DynamicCMInputOptions: update all dynamic children arrays, but skip parent_input (handled by DynamicCMInputOptions component)
      if (this.isDynamicCMInputOptions(input)) {
        const updated: DynamicCMInputOptions = { ...input }
        for (const [key, value] of Object.entries(input)) {
          if (
            Array.isArray(value) &&
            !['input_name', 'input_priority', 'parent_input'].includes(key)
          ) {
            updated[key] = value.map(updateRecursively)
          }
        }
        return updated
      }

      // Default: return as is
      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateRecursively))
  }

  // Updates the DynamicCMInputArray occurrence count
  updateDynamicCMInputArrayOccurrence(arrayInputParameterName: string, targetCount: number) {
    if (!arrayInputParameterName || targetCount < 0) return

    const updateArrayOccurrence = (input: CMInputType): typeof input => {
      if (
        this.isDynamicCMInputArray(input) &&
        input.input_parameter_name === arrayInputParameterName
      ) {
        const currentInputs = input.ArrayOfRegularInput || []
        const template = input.input_template
        const validatedCount = targetCount

        // Preserve existing inputs and their values, only add or remove as needed
        const updatedInputs = Array.from({ length: validatedCount }, (_, index) => {
          if (index < currentInputs.length) {
            // Keep existing input with its value
            return currentInputs[index]
          } else {
            // Create new input based on template
            return {
              ...structuredClone(template),
              input_name: `${template.input_name} ${index + 1}`,
              input_parameter_name: `${template.input_parameter_name}_${index}`,
            }
          }
        })
        return { ...input, ArrayOfRegularInput: updatedInputs }
      }

      // Recursively update children if they are DynamicCMInputOptions
      if (this.isDynamicCMInputOptions(input)) {
        const updatedOptions: Record<string, any> = {}
        for (const [key, value] of Object.entries(input)) {
          if (Array.isArray(value)) {
            updatedOptions[key] = value.map((item) => updateArrayOccurrence(item))
          } else {
            updatedOptions[key] = value
          }
        }
        return { ...input, ...updatedOptions }
      }

      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateArrayOccurrence))
  }

  // Updates a specific value in a DynamicCMInputArray
  updateDynamicCMInputArrayArrayOfRegularInput(
    arrayInputParameterName: string,
    newValue: string | number,
    index: number,
  ) {
    if (!arrayInputParameterName || index < 0) return

    const updateArrayValue = (input: CMInputType): typeof input => {
      if (
        this.isDynamicCMInputArray(input) &&
        input.input_parameter_name === arrayInputParameterName &&
        input.ArrayOfRegularInput &&
        index < input.ArrayOfRegularInput.length
      ) {
        const updatedInputs = [...input.ArrayOfRegularInput]
        updatedInputs[index] = { ...updatedInputs[index], input_value: newValue }
        return { ...input, ArrayOfRegularInput: updatedInputs }
      }

      // Recursively update children if they are DynamicCMInputOptions
      if (this.isDynamicCMInputOptions(input)) {
        const updatedOptions: Record<string, any> = {}
        for (const [key, value] of Object.entries(input)) {
          if (Array.isArray(value)) {
            updatedOptions[key] = value.map((item) => updateArrayValue(item))
          } else {
            updatedOptions[key] = value
          }
        }
        return { ...input, ...updatedOptions }
      }

      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateArrayValue))
  }

  // Updates the parent input and manages child options in DynamicCMInputOptions
  updateDynamicCMInputOptionsParentInput(
    value: string | number,
    _parentInput: CMInput,
    dynamicInputOptions: DynamicCMInputOptions,
  ) {
    const updateOptions = (input: CMInputType): typeof input => {
      if (
        this.isDynamicCMInputOptions(input) &&
        input.input_name === dynamicInputOptions.input_name
      ) {
        const updatedInput = { ...input } as DynamicCMInputOptions
        updatedInput.parent_input = { ...input.parent_input, input_value: value }
        return updatedInput
      }

      // Recursively update children if they are DynamicCMInputOptions
      if (this.isDynamicCMInputOptions(input)) {
        const updatedOptions = { ...input } as DynamicCMInputOptions
        for (const [key, value] of Object.entries(input)) {
          if (Array.isArray(value)) {
            updatedOptions[key] = value.map((item) => updateOptions(item))
          }
        }
        return updatedOptions
      }

      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateOptions))
  }

  // Update a CMLayerInput
  setLayerNeeded(layerInput: CMLayerInput, layerSelectedEvent: any) {
    // Does not hanlde layer input as array of DynamicCMInputArray

    const selectedLayerName = layerSelectedEvent.target.value
    const selectedLayer = layerInput.layers.find((layer) => layer.name === selectedLayerName)

    const updateRecursively = (input: CMInputType): typeof input => {
      // Update if this is the target CMLayerInput
      if (input === layerInput && this.isCMLayerInput(input)) {
        return { ...input, layerSelected: selectedLayer }
      }

      // Recurse into DynamicCMInputOptions
      if (this.isDynamicCMInputOptions(input)) {
        const updated: DynamicCMInputOptions = { ...input }
        for (const [key, value] of Object.entries(input)) {
          if (
            Array.isArray(value) &&
            !['input_name', 'input_priority', 'parent_input'].includes(key)
          ) {
            updated[key] = value.map(updateRecursively)
          }
        }
        return updated
      }

      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateRecursively))
  }

  // Update DynamicCMInputOptions with CMLayer as parent layer
  setLayerNeededParentInput(layerInput: CMLayerInput, selectedLayer: LayerSource) {
    const updateOptions = (input: CMInputType): typeof input => {
      // Handle DynamicCMInputOptions that have this layerInput as parent
      if (this.isDynamicCMInputOptions(input)) {
        if (input.parent_input === layerInput && this.isCMLayerInput(input.parent_input)) {
          const updatedInput = { ...input } as DynamicCMInputOptions
          updatedInput.parent_input = {
            ...input.parent_input,
            layerSelected: selectedLayer,
            input_value: (selectedLayer?.workspacename || selectedLayer.layer) ?? '', // personal layers have `layer` instead of `workspacename`
          }
          return updatedInput
        }

        // Recursively update nested dynamic options
        const updatedOptions = { ...input } as DynamicCMInputOptions
        for (const [key, value] of Object.entries(input)) {
          if (Array.isArray(value)) {
            updatedOptions[key] = value.map((item) => updateOptions(item))
          }
        }
        return updatedOptions
      }

      return input
    }

    this._setCmInputs(this.cmInputs$().map(updateOptions))
  }

  private async _pollCMStatus() {
    if (!this.isCMRunning$()) {
      clearTimeout(this._pollingTimeout)
      return
    }

    const response = await this.GETobject(
      this.currentCMTaskId$(),
      this._baseUrl + '/status/' + this.currentCMTaskId$(),
    )
    switch (response?.state) {
      case 'SUCCESS': {
        this.isCMRunning$.set(false)
        const currentPayload = this.currentCMPayload$()
        if (!currentPayload) {
          return
        }
        const newSession: CMSession = {
          cm_id: currentPayload.cm_id,
          payload: currentPayload,
          results: response.status.result,
          session_name: currentPayload.session_name,
          selected: 'true', // must be done here because of no layer CMs
        }
        this._cmSessionService.addNewCMSession(newSession)

        this.cmSelected$.set(null) // Back to list of CM
        // Scroll to sessionS of CM (no unique ID for each session)
        await setTimeout(() => {
          //Need to wait the end of back to list of cm
          const el = document.getElementById(newSession.cm_id)
          el?.scrollIntoView({ behavior: 'smooth' })
        }, 200)

        return response
      }
      case 'PENDING':
        if (this.isCMRunning$()) {
          this._pollingTimeout = setTimeout(() => this._pollCMStatus(), this._pollingInterval)
        }
        break
      case 'STARTED':
        if (this.isCMRunning$()) {
          this._pollingTimeout = setTimeout(() => this._pollCMStatus(), this._pollingInterval)
        }
        break
      case 'FAILURE':
        this.isCMRunning$.set(false)
        this._toasterService.openSnackBarError(`Error while running the CM: ${response?.status}`)
        throw new Error('Polling stopped: Task failed.')

      default:
        console.error('Unknown state:', response?.state)
        this.DELETE(this.currentCMTaskId$(), this._baseUrl + '/delete/' + this.currentCMTaskId$()) // stops backend tasks without "CM stopped" toaster
        this.isCMRunning$.set(false)
        this._toasterService.openSnackBarError('Unknown state encountered during polling.')
        throw new Error('Unknown state encountered during polling.')
    }
  }

  private _processLayerInput(input: CMLayerInput): LayerSource[] {
    //@todo, refactor to remove layer_type logic
    let allLayers: LayerSource[] = []

    for (const type of input.input_options_array) {
      const layers = this._layerService
        .layers$()
        .filter((x) => x.layer_type === type || x.workspacename === type)

      const hiddenLayers = this._layerService
        .hiddenLayers$()
        .filter((x) => x.layer_type === type || x.workspacename === type)

      if (
        this.isLoggedIn$() &&
        // Prevent error is click on cm before end of loading personal or shared layers
        this._uploadedLayersService.personalLayersState$().status === 'success' &&
        this._uploadedLayersService.sharedLayersState$().status === 'success'
      ) {
        const personalLayers = this._uploadedLayersService
          .personalLayers$()
          .filter((x) => x.layer === type || x.layer_type === type)

        const sharedLayers = this._uploadedLayersService
          .sharedLayers$()
          .filter((x) => x.layer === type || x.layer_type === type)

        allLayers = allLayers.concat(layers, hiddenLayers, personalLayers, sharedLayers)
      } else {
        allLayers = allLayers.concat(layers, hiddenLayers)
      }
    }
    return allLayers
  }
}
